import { useState, useEffect } from "react";
import { supabase, deleteImage, type GalleryImage } from "@/lib/supabase";
import ImageUploadField from "@/components/base/ImageUploadField";

export default function GalleryManager() {
  const [imageList, setImageList] = useState<GalleryImage[]>([]);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({ title: "", image_url: "", event_name: "" });
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);

  useEffect(() => { fetchImages(); }, []);

  const fetchImages = async () => {
    setIsLoading(true);
    const { data } = await supabase.from("gallery").select("*").order("date_uploaded", { ascending: false });
    setImageList(data || []);
    setIsLoading(false);
  };

  const filtered = imageList.filter(
    (img) =>
      img.title.toLowerCase().includes(search.toLowerCase()) ||
      img.event_name.toLowerCase().includes(search.toLowerCase())
  );

  const handleEdit = (img: GalleryImage) => {
    setFormData({ title: img.title, image_url: img.image_url, event_name: img.event_name });
    setEditingId(img.id);
    setShowModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    if (editingId) {
      const { error } = await supabase.from("gallery").update(formData).eq("id", editingId);
      if (!error) await fetchImages();
    } else {
      const { error } = await supabase.from("gallery").insert(formData);
      if (!error) await fetchImages();
    }
    setSaving(false);
    setShowModal(false);
    setEditingId(null);
    setFormData({ title: "", image_url: "", event_name: "" });
  };

  const handleDelete = async (img: GalleryImage) => {
    if (!confirm("Are you sure you want to delete this image?")) return;
    const { error } = await supabase.from("gallery").delete().eq("id", img.id);
    if (!error) {
      await deleteImage(img.image_url);
      await fetchImages();
    }
  };

  if (isLoading) {
    return (
      <div className="p-4 md:p-6 lg:p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-48" />
          <div className="h-10 bg-gray-100 rounded w-full max-w-md" />
          <div className="h-64 bg-gray-100 rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h1 className="font-heading font-bold text-2xl text-primary-900">Gallery Management</h1>
        <button onClick={() => { setEditingId(null); setFormData({ title: "", image_url: "", event_name: "" }); setShowModal(true); }} className="inline-flex items-center gap-2 px-4 py-2.5 bg-gold-600 hover:bg-gold-700 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap">
          <i className="ri-add-line" /> Add Image
        </button>
      </div>

      <div className="mb-4">
        <div className="relative max-w-md">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
          <input type="text" placeholder="Search gallery..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500" />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="px-4 md:px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Image</th>
                <th className="px-4 md:px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Title</th>
                <th className="px-4 md:px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Event</th>
                <th className="px-4 md:px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Date</th>
                <th className="px-4 md:px-6 py-3 text-xs font-semibold text-gray-500 uppercase text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((img) => (
                <tr key={img.id} className="hover:bg-gray-50/50">
                  <td className="px-4 md:px-6 py-3">
                    <button onClick={() => setLightboxImage(img.image_url)} className="w-12 h-12 md:w-14 md:h-14 rounded-lg object-cover cursor-pointer overflow-hidden block">
                      <img src={img.image_url} alt={img.title} className="w-full h-full object-cover hover:scale-110 transition-transform" />
                    </button>
                  </td>
                  <td className="px-4 md:px-6 py-3 text-sm font-medium text-gray-800">{img.title}</td>
                  <td className="px-4 md:px-6 py-3 text-sm text-gray-500">{img.event_name}</td>
                  <td className="px-4 md:px-6 py-3 text-sm text-gray-500 whitespace-nowrap">{new Date(img.date_uploaded).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</td>
                  <td className="px-4 md:px-6 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => handleEdit(img)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500 hover:text-primary-600 transition-colors" aria-label="Edit"><i className="ri-edit-line text-sm" /></button>
                      <button onClick={() => handleDelete(img)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-gray-500 hover:text-red-500 transition-colors" aria-label="Delete"><i className="ri-delete-bin-line text-sm" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && <div className="py-12 text-center text-gray-400 text-sm">No images found matching your search.</div>}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="font-heading font-bold text-lg text-primary-900">{editingId ? "Edit Image" : "Add Image"}</h2>
              <button onClick={() => setShowModal(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500"><i className="ri-close-line" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Title</label>
                <input type="text" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Event Name</label>
                <input type="text" value={formData.event_name} onChange={(e) => setFormData({ ...formData, event_name: e.target.value })} className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500" />
              </div>
              <ImageUploadField
                value={formData.image_url}
                onChange={(url) => setFormData({ ...formData, image_url: url })}
                folder="gallery"
                label="Gallery Image"
              />
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
              <button onClick={() => setShowModal(false)} className="px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">Cancel</button>
              <button onClick={handleSave} disabled={saving || !formData.image_url} className="px-5 py-2.5 bg-gold-600 hover:bg-gold-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-60">{saving ? "Saving..." : "Save"}</button>
            </div>
          </div>
        </div>
      )}

      {lightboxImage && (
        <div className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4" onClick={() => setLightboxImage(null)}>
          <button onClick={() => setLightboxImage(null)} className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white"><i className="ri-close-line" /></button>
          <img src={lightboxImage} alt="Preview" className="max-w-full max-h-[85vh] rounded-lg object-contain" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}